import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import PhoneInput from 'react-phone-input-2';
import { parsePhoneNumber } from 'libphonenumber-js';
import 'react-phone-input-2/lib/style.css';
import logo from '../assets/images/logo.svg';
import { useCountryDetection } from '../hooks/useCountryDetection';
import { useTranslations } from '../hooks/useTranslations';

import img1 from '../assets/images/card1.png';
import img2 from '../assets/images/card2.png';
import img3 from '../assets/images/card3.png';


// Объект с данными о курсах
const COURSES = {
  silver: {
    title: 'Silver',
    icon: img1,
  },
  gold: {
    title: 'Gold',
    icon: img2,
  },
  platinum: {
    title: 'Platinum',
    icon: img3,
  }
};

// Стили для PhoneInput (такие же как в Register.js)
const phoneInputCustomStyles = `
  .react-tel-input .form-control {
    width: 100% !important;
    height: 48px !important;
    border-radius: 16px !important;
    border: 1px solid #E5E7EB !important;
    font-size: 16px !important;
    padding-left: 48px !important;
    background: white !important;
  }

  .react-tel-input .form-control:focus {
    border-color: #277B6F !important;
    box-shadow: 0 0 0 1px #277B6F !important;
  }

  .react-tel-input .flag-dropdown {
    background: transparent !important;
    border: none !important;
    border-radius: 16px 0 0 16px !important;
  }

  .react-tel-input .selected-flag {
    background: transparent !important;
    border-radius: 16px 0 0 16px !important;
    width: 40px !important;
  }

  .react-tel-input .selected-flag:hover,
  .react-tel-input .selected-flag:focus {
    background: transparent !important;
  }

  .react-tel-input .country-list {
    border-radius: 12px !important;
    border: 1px solid #E5E7EB !important;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
    margin-top: 8px !important;
  }

  .react-tel-input .country-list .country:hover {
    background: #F3F4F6 !important;
  }

  .react-tel-input .country-list .country.highlight {
    background: #F3F4F6 !important;
  }
`;

function Checkout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const form = useRef(null);
  const { t } = useTranslations();

  // Получаем параметры из URL
  const courseType = searchParams.get('course')?.toLowerCase() || 'silver';
  const price = searchParams.get('price') || '0';

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false
  });

  const [errors, setErrors] = useState({});
  const { country: userCountry, isLoading: isCountryLoading } = useCountryDetection();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePhoneChange = (value, data) => {
    try {
      const phoneNumber = parsePhoneNumber('+' + value);
      if (phoneNumber) {
        const formattedNumber = phoneNumber.formatInternational();
        setFormData(prev => ({
          ...prev,
          phone: formattedNumber
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          phone: '+' + value
        }));
      }
    } catch (error) {
      setFormData(prev => ({
        ...prev,
        phone: '+' + value
      }));
    }

    if (errors.phone) {
      setErrors(prev => ({
        ...prev,
        phone: ''
      }));
    }
  };

  const validatePassword = (password) => {
    const hasNumber = /[0-9]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasMinLength = password.length >= 8;

    const errors = [];
    if (!hasNumber) errors.push(t('checkout.validation.digit'));
    if (!hasLowerCase) errors.push(t('checkout.validation.lowercase'));
    if (!hasUpperCase) errors.push(t('checkout.validation.uppercase'));
    if (!hasMinLength) errors.push(t('checkout.validation.minLength'));

    return errors;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = t('checkout.validation.firstNameRequired');
    } else if (/\d/.test(formData.firstName)) {
      newErrors.firstName = t('checkout.validation.firstNameNoNumbers');
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('checkout.validation.lastNameRequired');
    } else if (/\d/.test(formData.lastName)) {
      newErrors.lastName = t('checkout.validation.lastNameNoNumbers');
    }

    if (!formData.email) {
      newErrors.email = t('checkout.validation.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('checkout.validation.emailInvalid');
    }

    if (!formData.phone) {
      newErrors.phone = t('checkout.validation.phoneRequired');
    }

    if (!formData.password) {
      newErrors.password = t('checkout.validation.passwordRequired');
    } else {
      const passwordErrors = validatePassword(formData.password);
      if (passwordErrors.length > 0) {
        newErrors.password = t('checkout.validation.passwordRequirements', { requirements: passwordErrors.join(', ') });
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('checkout.validation.confirmPasswordRequired');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('checkout.validation.passwordsMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("https://api.maxprofitacademy.com/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          username: formData.firstName,
          lastname: formData.lastName,
          password: formData.password,
          email: formData.email,
          contact: formData.phone
        })
      });

      const data = await response.json();

      if (data.token) {
        localStorage.setItem("token", JSON.stringify(data.token));
        window.location.href = `/dashboard?course=${courseType}&price=${price}`;
      } else {
        setErrors(prev => ({
          ...prev,
          submit: data.message || t('checkout.validation.registrationError')
        }));
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error:', error);
      setErrors(prev => ({
        ...prev,
        submit: t('checkout.validation.registrationServerError')
      }));
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      <style>{phoneInputCustomStyles}</style>
      
      {/* Left side - Course Info */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-primary/5" />
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 p-8">
          <div className="text-center">
            <div className="text-6xl flex justify-center mb-6">
                <img className='w-40 h-40' src={COURSES[courseType]?.icon} alt={COURSES[courseType]?.title} />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
             {COURSES[courseType]?.title}
            </h3>
            {/* <p className="text-gray-600 text-lg mb-6">
              {COURSES[courseType]?.description}
            </p> */}
            <div className="text-4xl font-bold text-primary">
              ${price}
            </div>
          </div>
        </div>
      </div>

      {/* Мобильная версия инфо о курсе */}
      <div className="lg:hidden fixed top-0 inset-x-0 bg-white z-10 shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                className="w-16 h-16 object-contain" 
                src={COURSES[courseType]?.icon} 
                alt={COURSES[courseType]?.title} 
              />
              <div>
                <h3 className="font-bold text-gray-900">
                  {COURSES[courseType]?.title}
                </h3>
                <div className="text-2xl font-bold text-primary mt-1">
                  ${price}
                </div>
              </div>
            </div>
            
            {/* Декоративный элемент */}
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <div className="w-4 h-4 rounded-full bg-primary"></div>
              </div>
            </div>
          </div>

          {/* Прогресс бар */}
          <div className="mt-4 h-1 w-full bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full w-1/2 bg-primary rounded-full"></div>
          </div>
          <div className="mt-2 flex justify-between text-xs text-gray-500">
            <span>{t('checkout.progress.registration')}</span>
            <span>{t('checkout.progress.payment')}</span>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-8 pt-[160px] lg:pt-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <img src={logo} alt="MaxProfit Academy" className="h-12 hidden lg:block mx-auto mb-6" />
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">{t('checkout.registrationTitle')}</h2>
              {/* Добавим описание для мобильной версии */}
              <p className="mt-2 text-gray-600 text-sm lg:hidden">
                {t('checkout.registrationSubtitle')}
              </p>
            </div>
          </div>

          <form ref={form} onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder={t('checkout.firstNamePlaceholder')}
                  className={`w-full px-4 py-3 rounded-2xl bg-white border ${
                    errors.firstName ? 'border-red-500' : 'border-gray-200'
                  } text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors`}
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>
                )}
              </div>

              <div>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder={t('checkout.lastNamePlaceholder')}
                  className={`w-full px-4 py-3 rounded-2xl bg-white border ${
                    errors.lastName ? 'border-red-500' : 'border-gray-200'
                  } text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors`}
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('checkout.emailPlaceholder')}
                className={`w-full px-4 py-3 rounded-2xl bg-white border ${
                  errors.email ? 'border-red-500' : 'border-gray-200'
                } text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors`}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email}</p>
              )}
            </div>

            <div>
              <PhoneInput
                country={isCountryLoading ? null : userCountry}
                value={formData.phone}
                onChange={handlePhoneChange}
                containerClass="w-full"
                inputClass={`w-full ${errors.phone ? 'border-red-500' : 'border-gray-200'}`}
                buttonClass="phone-input-button"
                dropdownClass="phone-input-dropdown"
                enableSearch={true}
                searchPlaceholder={t('register.searchCountry')}
                searchNotFound={t('register.countryNotFound')}
                preferredCountries={['ru', 'by', 'ua', 'kz']}
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
              )}
            </div>

            <div className="relative">
              <input
                type={showPassword.password ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('checkout.passwordPlaceholder')}
                className={`w-full px-4 py-3 rounded-2xl bg-white border ${
                  errors.password ? 'border-red-500' : 'border-gray-200'
                } text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(prev => ({ ...prev, password: !prev.password }))}
                className="absolute right-4 top-[16px] text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword.password ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
              {errors.password && (
                <p className="mt-1 text-sm text-red-500">{errors.password}</p>
              )}
            </div>

            <div className="relative">
              <input
                type={showPassword.confirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t('checkout.confirmPasswordPlaceholder')}
                className={`w-full px-4 py-3 rounded-2xl bg-white border ${
                  errors.confirmPassword ? 'border-red-500' : 'border-gray-200'
                } text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(prev => ({ ...prev, confirmPassword: !prev.confirmPassword }))}
                className="absolute right-4 top-[16px] text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword.confirmPassword ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>
              )}
            </div>

            {errors.submit && (
              <p className="text-red-500 text-sm text-center">{errors.submit}</p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 px-4 bg-primary text-black font-medium rounded-2xl 
                          transition-all duration-200
                          ${isSubmitting 
                            ? 'opacity-70 cursor-not-allowed' 
                            : 'hover:bg-primary/90'}`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                  {t('checkout.registering')}
                </div>
              ) : (
                t('checkout.registerButton')
              )}
            </button>
          </form>

          {/* Добавим информацию о безопасности для мобильной версии */}
          <div className="mt-6 lg:hidden">
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>{t('checkout.secureRegistration')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout; 